import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { Colors, StyledComponents } from '../../themes';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import SitesActions from '../../redux/SitesRedux';
import PersonCountActions from '../../redux/PersonCountRedux';
import PersonsActions from '../../redux/PersonsRedux';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { Line } from 'rc-progress';
import Modal from '../common/Modal';
import SelectSitesModal from './SelectSitesModal';
import cloneDeep from 'lodash/cloneDeep';
import CircularProgress from '@material-ui/core/CircularProgress';

const { SpinningRefresh } = StyledComponents;
const MAX_SITES_NUMBER = 3; // if you want to display more/less sites, change this constant
const REFRESH_INTERVAL = 60; // interval in seconds

const styles = (theme) => ({
  widgetContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(4),
    paddingBottom: 0,
  },
  title: {
    margin: 0,
  },
  enableWidgetSwitch: {
    marginTop: -3,
  },
  selectorContainer: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '4px 4px 0 0',
  },
  button: {
    // flex: 1,
    fontWeight: 600,
    fontSize: 16,
    margin: '10px 10px 10px 0',
    color: Colors.white,
    border: `1px solid ${Colors.disabledGrey}`,
    '&:hover': {
      background: Colors.disabledGrey,
    },
  },
  noDataText: {
    marginTop: 40,
  },
  body: {
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    margin: 20,
    flexWrap: 'wrap',
    overflowY: 'auto',
    marginRight: 10,
    paddingRight: 10,
    maxHeight: 233,
    '&::-webkit-scrollbar': {
      width: '0.6em',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 10,
      backgroundColor: Colors.navBackgroundGrey,
    },
  },
  personCountRow: {
    marginBottom: 10,
  },
  textRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 600,
  },
  personCountNumber: {
    fontSize: 20,
  },
  clickToViewButton: {
    marginTop: 100,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

class PersonCounter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAutoRefreshOn: false,
      sites: [],
      selectedSites: [],
      timer: REFRESH_INTERVAL,
      // select sites modal
      isSitesModalOpen: false,
      sitesItemsPerPage: 10,
      sitesPageNumber: 0,
      sitesSearchValue: null,
      showMaxSitesError: false,
      showSpinner: true,
    };
    this.intervalTimer = null;
  }

  componentDidMount() {
    if (this.props.customerId) {
      this.sendGetSitesRequest();
      this.sendGetPersonsRequest();
    }
  }

  componentWillUnmount() {
    this.intervalTimer && clearInterval(this.intervalTimer);
    this.props.resetSites();
    this.props.resetPersonCount();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.customerId !== this.props.customerId) {
      this.props.resetSites();
      this.props.resetPersonCount();
      this.resetValues();
      if (this.props.customerId) {
        this.sendGetSitesRequest();
        this.sendGetPersonsRequest();
      }
    }
    if (
      this.props.customerId &&
      !this.props.sitesIsLoading &&
      prevProps.sitesIsLoading &&
      this.props.sites.length > 0
    ) {
      const selectedSites = this.props.sites.filter((site, index) => index < MAX_SITES_NUMBER);
      this.setState({ sites: this.props.sites, selectedSites }, () => this.sendGetPersonCountRequest());
    }
    if (prevProps.sites !== this.props.sites && this.props.sites.length === 0) {
      this.setState({ sites: [], selectedSites: [] });
    }
    if (!prevState.isAutoRefreshOn && this.state.isAutoRefreshOn) {
      this.props.totalCustomerPersons === 0 && this.sendGetPersonsRequest();
      this.loopCheckPersonCount();
    } else if (prevState.isAutoRefreshOn && !this.state.isAutoRefreshOn) {
      clearInterval(this.intervalTimer);
      this.setState({ timer: REFRESH_INTERVAL });
    }
  }

  sendGetSitesRequest = () => {
    const pageParams = {
      items: this.state.sitesItemsPerPage,
      page: this.state.sitesPageNumber,
      value: this.state.sitesSearchValue,
    };
    this.props.getSites(this.props.customerId, pageParams);
  };

  loopCheckPersonCount = () => {
    this.intervalTimer = setInterval(() => {
      const timer = this.state.timer > 0 ? this.state.timer - 1 : REFRESH_INTERVAL;
      if (!this.state.isSitesModalOpen) {
        if (this.state.timer === REFRESH_INTERVAL) {
          this.sendGetPersonCountRequest();
          this.setState({ showSpinner: false });
        }
        this.setState({ timer });
      }
    }, 1000);
  };

  sendGetPersonCountRequest = () => {
    const sitesData = this.state.sites.length > 0 ? this.state.sites : this.props.sites;
    const sites = sitesData.filter((site, index) => index < MAX_SITES_NUMBER).map(({ siteId }) => siteId);
    this.props.getPersonCount(this.props.customerId, sites);
  };

  sendGetPersonsRequest() {
    const pageParams = {
      items: 1,
      page: 0,
      value: null,
    };
    this.props.getPersons(this.props.customerId, pageParams);
  }

  resetValues = () => {
    this.setState({ selectedSites: [] });
  };

  refresh = () => {
    this.setState({ showSpinner: true });
    this.sendGetPersonCountRequest();
  };
  // Sites Modal methods
  changeSitesItemsPerPage = (sitesItemsPerPage) => {
    this.setState({ sitesItemsPerPage, sitesPageNumber: 0 }, () => this.sendGetSitesRequest());
  };

  changeSitesPage = (sitesPageNumber) => {
    this.setState({ sitesPageNumber }, () => this.sendGetSitesRequest());
  };

  handleSearchSites = (sitesSearchValue) => {
    this.setState({ sitesSearchValue, sitesPageNumber: 0 }, () => this.sendGetSitesRequest());
  };

  handleSearchSitesKeyPress = (event, value) => {
    if (event.key === 'Enter') {
      this.handleSearchSites(value);
    }
  };

  selectSite = (site) => {
    const previousSites = this.state.selectedSites;
    //check if the site was present in selectedSites
    const selectedIndex = previousSites.findIndex((item) => item.siteId === site.siteId);
    let newSelectedSites = [];

    if (selectedIndex === -1) {
      // if not found, add it
      newSelectedSites = newSelectedSites.concat(previousSites, site);
    } else if (selectedIndex === 0) {
      // if first item, remove it
      newSelectedSites = newSelectedSites.concat(previousSites.slice(1));
    } else if (selectedIndex === previousSites.length - 1) {
      // if last item, remove it
      newSelectedSites = newSelectedSites.concat(previousSites.slice(0, -1));
    } else if (selectedIndex > 0) {
      // if between first and last, remove it
      newSelectedSites = newSelectedSites.concat(
        previousSites.slice(0, selectedIndex),
        previousSites.slice(selectedIndex + 1)
      );
    }
    this.setState({
      selectedSites: newSelectedSites,
      showMaxSitesError: newSelectedSites.length > MAX_SITES_NUMBER,
    });
  };

  closeModalWithoutSave = () => {
    this.setState((prevState) => ({
      selectedSites: prevState.sites,
      isSitesModalOpen: false,
      timer: REFRESH_INTERVAL,
    }));
  };

  saveSelectedSites = () => {
    !this.state.showMaxSitesError &&
      this.setState(
        (prevState) => ({
          sites: prevState.selectedSites,
          isSitesModalOpen: false,
          timer: REFRESH_INTERVAL,
        }),
        () => this.refresh()
      );
  };

  render() {
    const { width } = this.props.size;
    const fullWidth = width - 40;
    const halfWidth = (this.props.size.width - 60) / 2;
    const { t, classes } = this.props;
    const sitesData = cloneDeep(this.props.sites).map((site) => ({ name: site.address.fullName, ...site }));

    return (
      <div className={classes.widgetContainer}>
        <Modal
          open={this.state.isSitesModalOpen}
          handleClose={this.closeModalWithoutSave}
          Content={SelectSitesModal}
          onClickSave={this.saveSelectedSites}
          showMaxSitesError={this.state.showMaxSitesError}
          saveButtonDisabled={this.state.showMaxSitesError}
          maxSitesNumber={MAX_SITES_NUMBER}
          // search sites table props
          checkboxColumn="start"
          data={sitesData}
          noDataText={t('controllerManager_noSite')}
          handleClick={(site) => this.selectSite(site)}
          columns={[{ title: t('common.name'), field: 'name', id: 'name' }]}
          handleSearch={this.handleSearchSites}
          activeRowField={'siteId'}
          checkedArray={this.state.selectedSites}
          searchPlaceholder={t('sites.searchSite')}
          itemsPerPage={this.state.sitesItemsPerPage}
          changeItemsPerPage={this.changeSitesItemsPerPage}
          totalElements={this.props.sitesTotalElements}
          page={this.state.sitesPageNumber}
          changePage={this.changeSitesPage}
          onKeyPress={this.handleSearchSitesKeyPress}
          // rows per page select props
          selectData={[5, 10, 20]}
          selectLabel={t('table.showPerPage')}
          selectValue={this.state.sitesItemsPerPage}
          onSelect={this.changeSitesItemsPerPage}
          selectWidth={90}
        />
        <div className={classes.titleRow}>
          <Typography variant="h2" className={classes.title}>
            {t('framesIndex_widget_personCounter')}
          </Typography>
          <FormControlLabel
            className={classes.enableWidgetSwitch}
            control={
              <Switch
                checked={this.state.isAutoRefreshOn}
                onChange={(event) => this.setState({ isAutoRefreshOn: event.target.checked })}
                value="isAutoRefreshOn"
              />
            }
            label={t('widgetInOutCounter_autoRefresh')}
            labelPlacement="start"
          />
        </div>
        <div className={classes.selectorContainer}>
          <Button
            onClick={() => this.setState({ isSitesModalOpen: true })}
            className={clsx(classes.button)}
            style={{ width: halfWidth }}
          >
            {t('widgetInOutCounter_selectSites')}
          </Button>

          <IconButton key="refresh" aria-label="refresh" color="inherit" onClick={this.refresh}>
            {this.props.isLoading ? <SpinningRefresh /> : <AutorenewIcon />}
          </IconButton>
        </div>

        {(this.props.personCountIsLoading || !this.props.totalCustomerPersons) && this.state.showSpinner ? (
          <div className={classes.noDataText}>
            <CircularProgress size={50} />
          </div>
        ) : this.props.personCountData.length === 0 ? (
          <div className={classes.noDataText}>{`- ${t('Global_noData')} -`}</div>
        ) : (
          <div className={clsx([classes.body])}>
            {this.props.personCountData
              .filter((item, index) => index < MAX_SITES_NUMBER)
              .map((site) => {
                return (
                  <div className={clsx([classes.personCountRow])} key={site.siteId}>
                    <div className={classes.textRow}>
                      <span className={classes.siteName}>{site.siteName}</span>
                      <span className={classes.personCountNumber}>{site.inCount}</span>
                    </div>
                    <Line
                      percent={(site.inCount / this.props.totalCustomerPersons) * 100}
                      strokeWidth="2"
                      trailWidth="2"
                      trailColor={Colors.black}
                      strokeColor={Colors.orange}
                      width={fullWidth}
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    distributorId: state.auth.distributorId,
    customerId: state.auth.customerId,
    sites: state.sites.sites,
    sitesIsLoading: state.sites.isLoading,
    sitesTotalElements: state.sites.totalElements,
    personCountData: state.personCount.personCount,
    totalCustomerPersons: state.persons.totalElements,
    personCountIsLoading: state.personCount.isLoading,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getSites: (customerId, pageParams) => dispatch(SitesActions.getSitesRequest(customerId, pageParams)),
  resetSites: () => dispatch(SitesActions.resetSites()),
  resetPersonCount: () => dispatch(PersonCountActions.resetPersonCount()),
  getPersonCount: (customerId, sites) => dispatch(PersonCountActions.getPersonCountRequest(customerId, sites)),
  getPersons: (customerId, pageParams) => dispatch(PersonsActions.getPersonsRequest(customerId, pageParams)),
});

export default compose(
  withTranslation(),
  withStyles(styles),
  withSize(),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(PersonCounter);
